/**
 * Group Quiz Client Component
 * Full-screen mobile-friendly interface for taking group quizzes
 */

import React, { useState, useEffect } from 'react';
import './GroupQuizClient.css';

interface Question {
    id: number;
    quizId: number;
    questionType: number;
    question: string;
    answer: string;
    answer_choices_json: string;
    answerExplanation?: string | null;
}

interface LeaderboardEntry {
    username: string;
    score: number;
}

interface GroupQuizClientProps {
    hash: string;
    username: string;
    onQuizEnd?: () => void;
}

type QuizState = 'waiting' | 'active' | 'submitted' | 'reveal' | 'completed';

export default function GroupQuizClient({ hash, username, onQuizEnd }: GroupQuizClientProps) {
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [quizState, setQuizState] = useState<QuizState>('waiting');
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [randomizedChoices, setRandomizedChoices] = useState<string[]>([]);
    const [userAnswer, setUserAnswer] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [correctAnswer, setCorrectAnswer] = useState('');
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [countdown, setCountdown] = useState(0);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState<'connected' | 'reconnecting' | 'disconnected'>('disconnected');
    const [reconnectAttempt, setReconnectAttempt] = useState(0);

    // Setup WebSocket connection with auto-reconnect
    useEffect(() => {
        let websocket: WebSocket | null = null;
        let reconnectTimeout: NodeJS.Timeout;
        let heartbeatInterval: NodeJS.Timeout;
        let isIntentionalClose = false;

        const connectWebSocket = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = `${protocol}//${window.location.host}/ws`;
            websocket = new WebSocket(wsUrl);

            websocket.onopen = () => {
                console.log(`[${username}] WebSocket connected`);
                setConnectionStatus('connected');
                setReconnectAttempt(0);

                websocket!.send(JSON.stringify({
                    type: 'join-room',
                    roomId: hash,
                    username: username,
                    isHost: false,
                }));

                // Start heartbeat - respond to server pings
                heartbeatInterval = setInterval(() => {
                    if (websocket?.readyState === WebSocket.OPEN) {
                        websocket.send(JSON.stringify({ type: 'pong' }));
                    }
                }, 30000);
            };

            websocket.onmessage = (event) => {
                const data = JSON.parse(event.data);
                console.log(`[${username}] Received message:`, data);

                switch (data.type) {
                    case 'quiz-started':
                        setTotalQuestions(data.totalQuestions);
                        break;

                    case 'show-question':
                        setCurrentQuestion(data.question);
                        setQuestionNumber(data.questionNumber);
                        setQuizState('active');
                        setUserAnswer('');
                        setIsCorrect(false);
                        setCorrectAnswer('');
                        break;

                    case 'session-sync':
                        // Handle reconnection sync
                        setCurrentQuestion(data.currentQuestion);
                        setQuestionNumber(data.questionNumber);
                        setLeaderboard(data.leaderboard);
                        if (data.hasAnswered) {
                            setQuizState('submitted');
                        } else {
                            setQuizState('active');
                        }
                        break;

                    case 'all-answered':
                        setQuizState('reveal');
                        setCorrectAnswer(data.correctAnswer);
                        setIsCorrect(data.isCorrect);
                        setLeaderboard(data.leaderboard);
                        break;

                    case 'next-question':
                        setCountdown(data.countdown);
                        break;

                    case 'quiz-completed':
                        setQuizState('completed');
                        setLeaderboard(data.leaderboard);
                        break;

                    case 'quiz-cancelled':
                        if (onQuizEnd) onQuizEnd();
                        break;
                }
            };

            websocket.onerror = (error) => {
                console.error(`[${username}] WebSocket error:`, error);
                setConnectionStatus('disconnected');
            };

            websocket.onclose = () => {
                console.log(`[${username}] WebSocket disconnected`);
                clearInterval(heartbeatInterval);

                if (!isIntentionalClose) {
                    // Attempt to reconnect with exponential backoff
                    setConnectionStatus('reconnecting');
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), 30000);
                    console.log(`[${username}] Reconnecting in ${delay}ms...`);

                    reconnectTimeout = setTimeout(() => {
                        setReconnectAttempt((prev) => prev + 1);
                        connectWebSocket();
                    }, delay);
                }
            };

            setWs(websocket);
        };

        connectWebSocket();

        return () => {
            isIntentionalClose = true;
            clearTimeout(reconnectTimeout);
            clearInterval(heartbeatInterval);
            if (websocket) {
                websocket.close();
            }
        };
    }, [hash, username, onQuizEnd, reconnectAttempt]);

    // Randomize multiple choice options when question changes
    useEffect(() => {
        if (currentQuestion && currentQuestion.questionType === 1) {
            try {
                const choices = JSON.parse(currentQuestion.answer_choices_json);
                if (Array.isArray(choices) && choices.length > 0) {
                    const shuffled = [...choices];
                    for (let i = shuffled.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                    }
                    setRandomizedChoices(shuffled);
                }
            } catch (error) {
                console.error('Failed to parse answer choices:', error);
                setRandomizedChoices([]);
            }
        } else {
            setRandomizedChoices([]);
        }
    }, [currentQuestion]);

    const handleSubmitAnswer = () => {
        if (!currentQuestion || !ws || quizState !== 'active') return;

        console.log(`[${username}] Submitting answer:`, userAnswer);
        setQuizState('submitted');

        ws.send(JSON.stringify({
            type: 'submit-answer',
            roomId: hash,
            username: username,
            questionId: currentQuestion.id,
            answer: userAnswer,
        }));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && quizState === 'active' && userAnswer.trim()) {
            handleSubmitAnswer();
        }
    };

    if (quizState === 'waiting') {
        return (
            <div className="groupQuizClientContainer">
                <div className="waitingScreen">
                    <h1>Get Ready!</h1>
                    <p>The quiz will start shortly...</p>
                    <div className="loadingSpinner"></div>
                </div>
            </div>
        );
    }

    if (quizState === 'completed') {
        const winner = leaderboard[0];
        const userEntry = leaderboard.find(entry => entry.username === username);
        const userRank = leaderboard.findIndex(entry => entry.username === username) + 1;

        return (
            <div className="groupQuizClientContainer">
                <div className="completedScreen">
                    <h1>Quiz Complete!</h1>
                    {winner && (
                        <div className="winnerAnnouncement">
                            <p className="winnerLabel">Winner</p>
                            <p className="winnerName">{winner.username}</p>
                            <p className="winnerScore">{winner.score} points</p>
                        </div>
                    )}
                    {userEntry && (
                        <div className="userResult">
                            <p>Your Rank: #{userRank}</p>
                            <p>Your Score: {userEntry.score} points</p>
                        </div>
                    )}
                    <div className="finalLeaderboard">
                        <h2>Final Standings</h2>
                        {leaderboard.map((entry, index) => (
                            <div
                                key={entry.username}
                                className={`leaderboardEntry ${entry.username === username ? 'currentUser' : ''}`}
                            >
                                <span className="rank">#{index + 1}</span>
                                <span className="name">{entry.username}</span>
                                <span className="score">{entry.score}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="groupQuizClientContainer">
            <div className="quizHeader">
                <div className="questionProgress">
                    Question {questionNumber} of {totalQuestions}
                </div>
                {connectionStatus !== 'connected' && (
                    <div className={`connectionStatus ${connectionStatus}`}>
                        {connectionStatus === 'reconnecting' ? '🔄 Reconnecting...' : '⚠️ Disconnected'}
                    </div>
                )}
            </div>

            <div className="questionSection">
                {currentQuestion && (
                    <>
                        <h2 className="questionText">{currentQuestion.question}</h2>

                        {quizState === 'active' && (
                            <div className="answerSection">
                                {currentQuestion.questionType === 1 ? (
                                    // Multiple Choice Question
                                    <div className="choicesContainer">
                                        {randomizedChoices.map((choice, index) => {
                                            const letter = String.fromCharCode(65 + index);
                                            return (
                                                <button
                                                    key={index}
                                                    className={`choiceButton ${
                                                        userAnswer === choice ? 'selected' : ''
                                                    }`}
                                                    onClick={() => setUserAnswer(choice)}
                                                >
                                                    <span className="choiceLetter">{letter}</span>
                                                    <span className="choiceText">{choice}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : currentQuestion.questionType === 2 ? (
                                    // True/False Question
                                    <div className="choicesContainer">
                                        <button
                                            className={`choiceButton ${
                                                userAnswer === 'True' ? 'selected' : ''
                                            }`}
                                            onClick={() => setUserAnswer('True')}
                                        >
                                            <span className="choiceLetter">T</span>
                                            <span className="choiceText">True</span>
                                        </button>
                                        <button
                                            className={`choiceButton ${
                                                userAnswer === 'False' ? 'selected' : ''
                                            }`}
                                            onClick={() => setUserAnswer('False')}
                                        >
                                            <span className="choiceLetter">F</span>
                                            <span className="choiceText">False</span>
                                        </button>
                                    </div>
                                ) : (
                                    // Short Answer Question
                                    <input
                                        type="text"
                                        className="answerInput"
                                        value={userAnswer}
                                        onChange={(e) => setUserAnswer(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter your answer..."
                                        autoFocus
                                    />
                                )}

                                <button
                                    className="submitButton"
                                    onClick={handleSubmitAnswer}
                                    disabled={!userAnswer.trim()}
                                >
                                    SUBMIT
                                </button>
                            </div>
                        )}

                        {quizState === 'submitted' && (
                            <div className="submittedState">
                                <div className="submittedIcon">✓</div>
                                <p>Answer Submitted</p>
                                <p className="waitingText">Waiting for other players...</p>
                                <div className="loadingSpinner"></div>
                            </div>
                        )}

                        {quizState === 'reveal' && (
                            <div className="revealState">
                                <p className={isCorrect ? 'resultCorrect' : 'resultIncorrect'}>
                                    {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                                </p>
                                <p className="correctAnswer">{correctAnswer}</p>
                                {currentQuestion.answerExplanation && (
                                    <div className="answerExplanation">
                                        <p className="explanationLabel">Explanation:</p>
                                        <p className="explanationText">
                                            {currentQuestion.answerExplanation}
                                        </p>
                                    </div>
                                )}
                                {countdown > 0 && (
                                    <p className="countdownText">
                                        Next question in {countdown} seconds...
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {leaderboard.length > 0 && (
                <div className="leaderboardBar">
                    <div className="leaderboardTitle">Leaderboard</div>
                    <div className="leaderboardScroll">
                        {leaderboard.map((entry, index) => (
                            <div
                                key={entry.username}
                                className={`leaderboardItem ${entry.username === username ? 'currentUser' : ''}`}
                            >
                                <span className="position">#{index + 1}</span>
                                <span className="playerName">{entry.username}</span>
                                <span className="playerScore">{entry.score}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
